// src/i18n/fr/views/dominioPropio.js
//
// Traducción al francés de la guía que ve el anfitrión para apuntar su dominio a
// la web montada en el constructor. Misma estructura y mismas claves que la
// versión en español.
// Marcadores: {dominio} {destino} {nombre} {tipo} {valor} {encontrado} {actual}
//             {sitio} {panel} {panelCorto} {paso}

export const dominioPropio = {
  cabecera: {
    titulo: "Connectez votre propre domaine",
    subtitulo:
      "Votre site est visible pour l'instant sur {actual}. À la fin, il s'affichera sur votre adresse habituelle.",
    duracion: "Dix minutes de votre côté. Le reste, internet s'en charge tout seul.",
  },

  // Los tres pasos del asistente.
  pasos: {
    uno: "Votre domaine",
    dos: "Les deux lignes dans {panelCorto}",
    tres: "Vérifier",
    unoDetalle: "Vous saisissez l'adresse que vous voulez utiliser",
    dosDetalle: "Vous copiez deux lignes dans le panneau de votre domaine",
    tresDetalle: "Nous vérifions que tout est en place",
    indicador: "Étape {paso} sur 3",
  },

  // ── Paso 1: escribir el dominio ────────────────────────────────────────────
  paso1: {
    titulo: "Quelle adresse voulez-vous pour votre site ?",
    descripcion:
      "Saisissez le domaine que vous avez déjà acheté. Nous ne vendons pas de domaines : cette page connecte un domaine qui est déjà le vôtre.",
    etiqueta: "Votre domaine",
    marcador: "mondomaine.com",
    ayuda: "Écrivez-le tel quel, sans « https:// » et sans barre oblique à la fin.",

    formaTitulo: "Et sous quelle forme voulez-vous l'afficher ?",
    formaRaiz: {
      titulo: "mondomaine.com",
      descripcion: "L'adresse courte, sans rien devant. C'est la plus courante.",
    },
    formaWww: {
      titulo: "www.mondomaine.com",
      descripcion: "La forme classique, avec « www » devant.",
    },
    formaSubdominio: {
      titulo: "reservations.mondomaine.com",
      descripcion:
        "Une adresse à part, pour ne pas toucher au site que vous avez déjà sur {dominio}.",
    },
    subdominioEtiqueta: "Le mot qui va devant",
    subdominioMarcador: "reservations",
    subdominioAyuda: "Le mot seul. Nous ajoutons le reste.",

    avisoWebExistente: {
      titulo: "Vous avez déjà un site à cette adresse ?",
      texto:
        "Si {dominio} affiche aujourd'hui un autre site, il cessera de s'afficher à la fin de l'opération et c'est celui-ci qui prendra sa place. Si vous préférez le garder, utilisez une adresse à part comme reservations.{dominio}.",
    },

    boton: "Continuer",
    botonComprobando: "Vérification du domaine…",
  },

  // ── Dónde tiene el dominio ─────────────────────────────────────────────────
  proveedor: {
    pregunta: "Où avez-vous acheté ou gérez-vous {dominio} ?",
    ayuda:
      "C'est l'entreprise à qui vous payez le renouvellement chaque année. Si vous ne vous en souvenez pas, cherchez dans votre boîte mail la dernière facture du domaine.",
    cloudflare: "Cloudflare",
    cloudflareDetalle: "Instructions complètes, écran par écran",
    otro: "Une autre entreprise",
    otroDetalle: "GoDaddy, IONOS, Namecheap, OVH…",
    otroTexto:
      "Les lignes à ajouter sont exactement les mêmes. Seul change l'endroit où se trouve le bouton. Cherchez dans votre panneau quelque chose qui indique « DNS », « Zone DNS » ou « Gestion DNS » et copiez-y les lignes du tableau ci-dessus, une par une.",
    noLoSe: "Je ne sais pas",
    noLoSeTexto:
      "Écrivez-nous avec votre domaine et nous vous dirons où il se trouve et ce qu'il faut modifier.",
  },

  // ── Paso 2: los registros ──────────────────────────────────────────────────
  paso2: {
    titulo: "Il faut maintenant dire à votre domaine où se trouve votre site",
    descripcion:
      "Votre domaine est un nom. Seul, il ne mène nulle part : quelqu'un doit noter dans un carnet que ce nom correspond à notre serveur. Ce carnet s'appelle le DNS et il se trouve dans le panneau de votre domaine.",

    queEsUnRegistro: {
      titulo: "Ce que vous allez faire exactement",
      texto:
        "Vous allez ajouter une ligne à ce carnet. Une seule. Elle a trois cases : comment elle s'appelle, vers où elle pointe et si elle passe ou non par un filtre. Rien de plus.",
      textoOtro:
        "Vous allez ajouter des lignes à ce carnet. Chacune a deux cases : comment elle s'appelle et vers où elle pointe. Rien de plus.",
    },

    copiaTitulo: "Copiez ces données telles quelles",
    copiaAyuda:
      "Utilisez les boutons de copie. Un espace en trop ou une lettre changée et cela ne marche pas.",

    tabla: {
      tipo: "Type",
      nombre: "Nom",
      destino: "Cible",
      contenido: "Contenu",
      proxy: "État du proxy",
      ttl: "TTL",
      valorProxy: "DNS uniquement (nuage gris)",
      valorTtl: "Auto",
    },

    copiar: "Copier",
    copiado: "Copié",
    copiarTodo: "Copier les données",

    tipos: {
      cnameTitulo: "CNAME",
      cnameTexto:
        "C'est un renvoi : il indique que votre domaine est servi sur {destino}. Si un jour nous changeons de serveur, vous n'avez rien à modifier.",
      txtTitulo: "TXT",
      txtTexto:
        "C'est une note signée. Elle ne mène nulle part : elle prouve seulement que le domaine est bien le vôtre. Vous pouvez la supprimer une fois le site en ligne, mais il est plus simple de la laisser.",
    },

    nombreTitulo: "Ce qu'il faut écrire dans « Nom »",
    nombreRaiz:
      "Pour le domaine court ({dominio}), écrivez une arobase : @. Dans le carnet du DNS, l'arobase signifie « le domaine seul, sans rien devant ». N'écrivez pas {dominio} en entier.",
    nombreWww: "Pour www.{dominio}, écrivez seulement : www",
    nombreSubdominio:
      "Pour {nombre}.{dominio}, écrivez seulement le mot de devant : {nombre}",
    nombreAviso:
      "Dans Cloudflare, vous n'écrivez que cette partie. Il ajoute « .{dominio} » de lui-même et vous le montre sous le champ.",
    nombreAvisoOtro:
      "Dans presque tous les panneaux, on n'écrit que cette partie et ils ajoutent « .{dominio} ». Si le vôtre vous demande le nom complet, écrivez {nombre}.{dominio}.",
    nombreAvisoOtroRaiz:
      "Si votre panneau n'accepte pas « @ », laissez le nom vide : chez beaucoup, cela veut dire la même chose, le domaine sans rien devant.",

    volver: "C'est fait, revenir à WACheckin",
    ayudaEnviar: "M'envoyer ces données par e-mail",
    ayudaEnviarTexto:
      "Si c'est votre informaticien ou la personne qui s'est occupée de votre site qui va le faire, nous envoyons les instructions à votre adresse e-mail pour que vous les lui transfériez.",
  },

  // ── Cloudflare, paso a paso ────────────────────────────────────────────────
  cloudflare: {
    titulo: "Faites-le dans Cloudflare",
    subtitulo: "Dix minutes. Ayez sous la main l'adresse e-mail et le mot de passe de Cloudflare.",

    pasos: {
      uno: "Connectez-vous à dash.cloudflare.com avec votre compte.",
      dos: "Le premier écran affiche vos domaines. Cliquez sur {dominio}.",
      tres:
        "Dans le menu de gauche, ouvrez « DNS » puis « Enregistrements » (« Records »).",
      cuatro:
        "Vous verrez la liste des lignes que vous avez déjà. Cliquez sur le bouton « Ajouter un enregistrement » (« Add record »).",
      cinco: "Dans « Type » (« Type »), choisissez {tipo} dans la liste déroulante.",
      seis: "Dans « Nom » (« Name »), écrivez {nombre}",
      siete: "Dans « Cible » (« Target »), collez {valor}",
      ocho:
        "Dans « État du proxy » (« Proxy status »), cliquez sur le nuage orange. Il doit devenir gris et afficher « DNS uniquement » (« DNS only »).",
      nueve: "Laissez « TTL » sur « Auto ». N'y touchez pas.",
      diez: "Cliquez sur « Enregistrer » (« Save »).",
      once:
        "Si le tableau ci-dessus contient plus d'une ligne, reprenez à l'étape 4 avec la suivante.",
    },

    hecho: "C'est terminé. Revenez sur WACheckin et cliquez sur « Vérifier ».",

    avisoAntes: {
      titulo: "Avant d'enregistrer, lisez ceci",
      texto:
        "Cloudflare met ce nuage en orange tout seul, sans rien demander. Si vous le laissez orange, la connexion ne marchera pas. L'étape 8 est la plus importante de tout cet écran.",
    },

    accesoNegado: {
      titulo: "Vous n'arrivez pas à entrer dans Cloudflare ?",
      texto:
        "Le compte est souvent au nom de la personne qui a créé votre site. Demandez-lui de faire ces étapes ou de vous ajouter comme membre du domaine.",
    },
  },

  // ── La nube naranja ────────────────────────────────────────────────────────
  proxy: {
    titulo: "Le nuage orange : c'est là que ça coince presque toujours",
    queEs:
      "À côté de chaque ligne du DNS, Cloudflare affiche un petit nuage. Orange signifie que le trafic passe d'abord par Cloudflare. Gris signifie qu'il va directement.",
    porQueFalla:
      "Avec le nuage orange, quand quelqu'un demande votre domaine, Cloudflare ne répond pas {destino} : il répond avec ses propres adresses. Notre vérification pose la question, tombe sur Cloudflare et ne trouve pas la ligne que nous vous avons demandée. Même si vous l'avez bien écrite, pour nous c'est comme si elle n'existait pas.",
    porQueFalla2:
      "Et il y a une deuxième conséquence, pire : le cadenas de sécurité de votre site est délivré en vérifiant que le domaine arrive bien jusqu'à nous. Avec le nuage orange il n'arrive pas, donc le cadenas ne peut pas être délivré et le navigateur préviendra que le site n'est pas sûr.",
    comoSeArregla:
      "Cliquez sur le nuage. Il devient gris et le texte à côté passe à « DNS uniquement » (« DNS only »). Enregistrez. C'est tout.",
    grisEsSeguro:
      "Le mettre en gris ne laisse pas votre site sans protection. Votre site, c'est nous qui le servons, avec son certificat et derrière notre propre protection. Cloudflare restait au milieu simplement parce qu'il est configuré ainsi au départ.",
    ttlBloqueado:
      "Un détail qui fait perdre du temps : tant que le nuage est orange, Cloudflare ne vous laisse pas modifier le TTL. En le passant en gris, le champ se débloque. Laissez-le sur « Auto ».",
    etiquetaProxied: "Avec proxy (« Proxied »), nuage orange : NON",
    etiquetaSoloDns: "DNS uniquement (« DNS only »), nuage gris : OUI",
  },

  // ── El dominio corto (raíz) ────────────────────────────────────────────────
  raiz: {
    titulo: "Si vous utilisez le domaine court : vous n'avez rien de spécial à faire",
    texto:
      "Une vieille règle d'internet dit que le domaine court ({dominio}, sans rien devant) n'accepte pas ce type de ligne. Chez la plupart des entreprises, cela oblige à faire des détours.",
    flattening:
      "Cloudflare s'en occupe tout seul. Quand le « Nom » est @, il applique une méthode à lui, le CNAME flattening : il garde votre renvoi et, quand quelqu'un pose la question, il répond directement avec l'adresse finale. Vous n'avez rien à activer, c'est inclus dans toutes les formules, y compris la gratuite.",
    avisoAjusteGlobal:
      "Ce qu'il vaut mieux faire : ne touchez pas à l'option « CNAME flattening for all CNAME records », dans « DNS » → « Configuration » (« Settings »). Appliquée à tout, elle cesse de répondre les renvois tels qu'ils sont écrits et casse les vérifications de propriété d'autres services que vous utilisez : la messagerie, Google, la boutique…",
    tituloOtro: "Si vous utilisez le domaine court ({dominio})",
    textoOtroA:
      "Sur le domaine sans rien devant, on met des lignes de type A, celles du tableau. Toutes les entreprises les acceptent.",
    textoOtroCname:
      "Beaucoup d'entreprises ne permettent pas de mettre un CNAME sur le domaine sans rien devant. Si la vôtre ne vous laisse pas enregistrer cette ligne, utilisez « www » ou une adresse à part comme reservations.{dominio}, ou écrivez-nous et nous réglerons cela avec vous.",
    conflicto: {
      titulo: "Si le domaine court avait déjà quelque chose",
      texto:
        "Un même nom ne peut pas avoir deux destinations en même temps. Si la liste contient déjà une ligne de type A ou CNAME avec le « Nom » sur @ (ou sur « www », si vous allez utiliser www), supprimez-la avant d'ajouter la nôtre. Cloudflare vous préviendra si vous ne le faites pas.",
      textoOtro:
        "Un même nom ne peut pas avoir deux destinations en même temps. Si votre panneau contient déjà une ligne de type A ou CNAME avec ce même nom, supprimez-la avant d'ajouter la nôtre.",
      comoBorrar:
        "Passez la souris sur cette ligne, cliquez sur « Modifier » (« Edit ») puis sur « Supprimer » (« Delete »).",
      apunta:
        "Notez sur un papier ce qu'il y avait avant de le supprimer, au cas où il faudrait revenir en arrière.",
    },
  },

  // ── Cuánto tarda ───────────────────────────────────────────────────────────
  propagacion: {
    titulo: "Au bout de combien de temps est-ce visible ?",
    texto:
      "Cloudflare publie le changement tout de suite. Ce qui prend du temps, c'est que le reste d'internet soit au courant : chaque ordinateur garde une copie du carnet pendant un moment pour ne pas poser mille fois la question.",
    plazoNormal:
      "Avec le TTL sur « Auto », Cloudflare demande que cette copie dure cinq minutes. En pratique, cela marche en général entre cinq et trente minutes après l'enregistrement.",
    textoOtro:
      "Votre panneau publie le changement en quelques minutes. Ce qui prend du temps, c'est que le reste d'internet soit au courant : chaque ordinateur garde une copie du carnet pendant un moment pour ne pas poser mille fois la question.",
    plazoNormalOtro:
      "En général, cela marche entre dix minutes et une heure après l'enregistrement. Certaines entreprises mettent plus longtemps à publier le changement : comptez jusqu'à 24 heures avant de vous inquiéter.",
    plazoMalo:
      "Si votre domaine affichait déjà un autre site, votre propre ordinateur peut mettre plus de temps à lâcher ce qu'il avait en mémoire. Ce n'est pas une panne. Comptez 24 heures avant de vous inquiéter.",
    truco:
      "Une astuce pour ne pas y passer la journée : ouvrez-le dans une fenêtre de navigation privée, ou depuis votre téléphone en données mobiles plutôt qu'en wifi. Vous voyez ainsi ce que voit un nouveau client, et non ce que votre ordinateur a gardé en mémoire.",
    dominioNuevo:
      "Autre chose : si vous venez d'ajouter le domaine à Cloudflare pour la première fois, Cloudflare met jusqu'à 24 heures à le considérer comme actif. En attendant, tout fonctionne comme si les nuages étaient gris, même si vous les voyez orange. Attendez que le domaine affiche « Actif » (« Active ») avant de conclure que quelque chose ne va pas.",
    ssl:
      "Quand la vérification sera bonne, nous délivrons le cadenas de sécurité. Cela prend quelques minutes de plus. Jusqu'à la fin, le navigateur peut prévenir que le site n'est pas sûr : c'est normal et cela passe tout seul.",
  },

  // ── Paso 3: comprobar ──────────────────────────────────────────────────────
  paso3: {
    titulo: "Nous allons vérifier",
    descripcion:
      "Nous regardons votre domaine et nous vous disons ce que nous trouvons. S'il manque quelque chose, nous vous disons quoi et comment le corriger.",
    boton: "Vérifier maintenant",
    botonReintentar: "Vérifier à nouveau",
    comprobando: "Nous regardons vos enregistrements…",
    comprobandoAyuda: "Cela prend quelques secondes.",

    resultado: {
      registroOk: "La ligne du DNS est en place et pointe vers nous",
      registroKo: "Nous ne voyons pas encore la ligne",
      proxyOk: "Le nuage est gris, comme il doit l'être",
      proxyKo: "Le nuage est orange",
      sslOk: "Cadenas de sécurité délivré",
      sslPendiente: "Délivrance du cadenas de sécurité…",
      webOk: "Votre site s'ouvre sur {dominio}, avec le cadenas",
      webActivando: "Nous activons votre site sur votre domaine, avec son cadenas de sécurité",
      webDespues: "Ensuite, nous vérifions que votre site s'ouvre, avec le cadenas",
    },

    activando: {
      titulo: "Votre domaine pointe déjà au bon endroit. Nous activons votre site",
      texto:
        "Il ne manque que le cadenas de sécurité, qui est délivré tout seul et prend en général une ou deux minutes. Ne changez rien dans {panel} : c'est nous qui vérifions et vous verrez ici quand ce sera prêt.",
    },

    exito: {
      titulo: "C'est bon, votre site est sur {dominio}",
      texto:
        "Vous pouvez taper l'adresse dans le navigateur et le voir. L'ancienne adresse {actual} continue de fonctionner, donc les liens que vous aviez déjà distribués ne se cassent pas.",
      verWeb: "Voir mon site",
      soloWww: "Votre site s'ouvre à l'adresse avec « www ». Si vous souhaitez qu'il fonctionne aussi sans « www », demandez à votre fournisseur de domaine une redirection vers l'adresse avec « www », ou un enregistrement ALIAS/ANAME à la racine si son panneau le permet.",
      copiarEnlace: "Copier l'adresse",
      siguiente: "Mettez-la maintenant sur vos profils Booking, Airbnb, Instagram et dans votre signature d'e-mail.",
    },

    pendiente: {
      titulo: "Ce n'est pas encore prêt, et c'est normal",
      texto:
        "Si vous venez de l'enregistrer dans {panel}, laissez passer quelques minutes et cliquez à nouveau. Vous n'êtes pas obligé de rester ici : nous vous prévenons par e-mail dès que c'est prêt.",
      avisarme: "Me prévenir par e-mail quand ce sera prêt",
    },
  },

  // ── Estados del dominio en la ficha ────────────────────────────────────────
  estados: {
    sinConectar: "Non connecté",
    pendiente: "En attente des lignes DNS",
    comprobando: "Vérification en cours",
    emitiendoSsl: "Délivrance du cadenas",
    activo: "En service",
    error: "En erreur",
    activoAyuda: "Votre site s'affiche sur {dominio} et la connexion est sécurisée.",
    activandoAyuda: "Votre domaine pointe déjà au bon endroit. Dans quelques minutes, votre site s'ouvrira sur {dominio}, avec le cadenas.",
  },

  // ── Errores ────────────────────────────────────────────────────────────────
  errores: {
    formatoInvalido: {
      titulo: "Cela ne ressemble pas à un domaine",
      texto: "Écrivez-le ainsi : mondomaine.com. Sans espaces et sans rien d'autre.",
    },
    conProtocolo: {
      titulo: "Le « https:// » est en trop",
      texto: "Enlevez-le et gardez seulement le nom : {dominio}",
    },
    conRuta: {
      titulo: "Tout ce qui suit la barre oblique est en trop",
      texto: "Gardez seulement la partie de devant : {dominio}",
    },
    sinExtension: {
      titulo: "Il manque la terminaison",
      texto: "Un domaine se termine par .com, .fr, .net… Vérifiez que vous ne l'avez pas coupé.",
    },
    dominioNoExiste: {
      titulo: "Ce domaine n'existe pas",
      texto:
        "Personne n'est enregistré sous ce nom. Vérifiez l'orthographe. Si vous venez de l'acheter, attendez quelques heures et réessayez.",
    },
    dominioCaducado: {
      titulo: "Ce domaine semble expiré",
      texto:
        "Connectez-vous chez l'entreprise où vous l'avez acheté et vérifiez que le renouvellement est payé. Tant qu'il est expiré, il ne peut pas être connecté.",
    },
    yaUsadoOtraCuenta: {
      titulo: "Ce domaine est déjà connecté à un autre compte",
      texto:
        "Un domaine ne peut pointer que vers un seul site à la fois. Si {dominio} est bien le vôtre, écrivez-nous et nous le déconnectons de l'autre compte pour que vous puissiez l'utiliser ici.",
      contactar: "Écrire au support",
    },
    yaUsadoEstaCuenta: {
      titulo: "Vous l'avez déjà connecté",
      texto:
        "{dominio} est utilisé sur « {sitio} ». Déconnectez-le de là ou choisissez une autre adresse pour ce site.",
    },
    registrosNoAparecen: {
      titulo: "Nous ne voyons encore rien sur votre domaine",
      texto:
        "C'est souvent une question de minutes : vous venez de l'enregistrer et cela n'est pas encore arrivé partout. Attendez dix minutes et cliquez à nouveau sur « Vérifier ».",
      repasa: "Si une demi-heure est passée, vérifiez trois choses dans {panel} :",
      repasa1: "Que dans « Nom » vous avez mis {nombre} et non le domaine entier.",
      repasa2: "Que vous avez cliqué sur « Enregistrer » (« Save ») et que la ligne apparaît dans la liste.",
      repasa3: "Que vous l'avez écrite sur {dominio} et non sur un autre domaine de votre compte.",
    },
    proxyActivado: {
      titulo: "Le nuage orange est activé",
      texto:
        "Nous voyons votre domaine, mais il passe par le proxy de Cloudflare et cela nous cache la destination réelle. Tant qu'il reste orange, ni la vérification ni le cadenas de sécurité ne peuvent fonctionner.",
      solucion:
        "Allez dans « DNS » → « Enregistrements », cherchez la ligne portant le nom {nombre}, cliquez sur le nuage orange jusqu'à ce qu'il devienne gris et affiche « DNS uniquement » (« DNS only »), puis enregistrez. Revenez ensuite et cliquez sur « Vérifier ».",
    },
    apuntaOtroSitio: {
      titulo: "Votre domaine pointe vers un autre endroit",
      texto:
        "Nous avons trouvé {encontrado} et nous attendions {destino}. C'est presque toujours une ancienne ligne de l'ancien site qui est restée là.",
      solucion:
        "Dans « DNS » → « Enregistrements », supprimez l'ancienne ligne portant le nom {nombre} et ne gardez que la nôtre.",
    },
    destinoAntiguo: {
      titulo: "La valeur que nous vous avions donnée ne fonctionnait pas",
      texto:
        "Votre ligne mène à {encontrado}, c'est bien ce que nous vous indiquions, mais cette adresse ne fonctionne pas. L'erreur vient de nous, pas de vous.",
      solucion: "Dans {panel}, modifiez la ligne {nombre} et mettez {destino} comme valeur. Laissez le TXT tel quel.",
    },
    registroDuplicado: {
      titulo: "Il y a deux lignes pour le même nom",
      texto:
        "Un nom ne peut pas mener à deux endroits. Supprimez celle qui n'est pas la nôtre et vérifiez à nouveau.",
    },
    error1014: {
      titulo: "Si le navigateur affiche « Error 1014 »",
      texto:
        "C'est une autre face du même problème habituel : le nuage orange. Mettez-le en gris (« DNS uniquement ») et il disparaît.",
    },
    sslFallido: {
      titulo: "Nous n'avons pas pu délivrer le cadenas de sécurité",
      texto:
        "La ligne du DNS est bonne, mais le cadenas ne sort pas. C'est souvent encore le nuage orange, ou une ancienne ligne qui nous gêne. Réessayez dans un moment ; si c'est toujours pareil, écrivez-nous et nous regarderons nous-mêmes.",
      reintentar: "Relancer la délivrance",
    },
    sinPermisos: {
      titulo: "Vous ne pouvez pas changer le domaine",
      texto: "Demandez-le au responsable du compte.",
    },
    servidor: {
      titulo: "Nous n'avons pas pu le vérifier pour le moment",
      texto: "Quelque chose n'a pas marché de notre côté. Réessayez dans quelques minutes.",
    },
    guardar: "Le domaine n'a pas pu être enregistré. Réessayez.",
  },

  // ── Desconectar ────────────────────────────────────────────────────────────
  desconectar: {
    boton: "Déconnecter ce domaine",
    titulo: "Déconnecter {dominio} ?",
    texto:
      "Votre site cessera de s'afficher sur {dominio} et ne sera plus visible que sur {actual}. Les personnes qui taperont votre domaine ne trouveront rien tant que vous ne l'aurez pas fait pointer ailleurs.",
    aviso: "Les lignes que vous avez ajoutées dans {panel} sont toujours là. Supprimez-les vous-même si vous n'en voulez plus.",
    cancelar: "Annuler",
    confirmar: "Déconnecter",
    hecho: "Domaine déconnecté.",
  },

  // ── Dudas ──────────────────────────────────────────────────────────────────
  dudas: {
    titulo: "Les questions qui reviennent toujours",

    correoPregunta: "Est-ce que cela va couper les e-mails de @{dominio} ?",
    correoTexto:
      "Non. Les e-mails passent par d'autres lignes du carnet, celles de type MX, et nous n'y touchons pas. Tant que vous ne les supprimez pas, votre messagerie reste identique.",

    otraWebPregunta: "J'ai un autre site sur ce domaine. Que devient-il ?",
    otraWebTexto:
      "Il cesse de s'afficher : le domaine montre désormais celui-ci. Si vous voulez garder les deux, utilisez une adresse à part comme reservations.{dominio} pour celui de WACheckin.",

    volverAtrasPregunta: "Puis-je revenir en arrière ?",
    volverAtrasTexto:
      "Oui, quand vous voulez. On défait là où on a fait : vous supprimez la ligne dans {panel} et vous remettez celle que vous aviez. Cela met le même temps à se voir, quelques minutes.",

    wwwPregunta: "Est-ce que cela marchera avec et sans « www » ?",
    wwwTexto:
      "Oui, si vous ajoutez les deux lignes que nous vous donnons. L'une commande et l'autre renvoie vers la première, pour que Google ne voie pas deux sites identiques.",

    costePregunta: "Est-ce que cela me coûte quelque chose ?",
    costeTexto:
      "Le connecter, non. Le domaine, vous continuez à le payer là où vous l'avez acheté, comme jusqu'à présent.",

    quienPregunta: "Je ne me sens pas capable de faire cela.",
    quienTexto:
      "C'est normal, c'est la partie la plus ingrate de tout le processus. Écrivez-nous avec votre domaine et nous le ferons avec vous par téléphone en dix minutes.",
  },

  // ── Botonera ───────────────────────────────────────────────────────────────
  botones: {
    atras: "Retour",
    siguiente: "Suivant",
    guardar: "Enregistrer",
    guardando: "Enregistrement…",
    cerrar: "Fermer",
    entendido: "J'ai compris",
    abrirCloudflare: "Ouvrir Cloudflare",
    soporte: "Demander l'aide de WACheckin",
    verGuia: "Revoir le guide",
  },

  avisos: {
    copiado: "Copié dans le presse-papiers",
    copiadoError: "Impossible de copier. Sélectionnez-le à la main.",
    guardado: "Domaine enregistré. Il faut maintenant ajouter les lignes dans {panel}.",
    conectado: "Votre site s'affiche sur {dominio} !",
    correoEnviado: "Nous vous avons envoyé les instructions par e-mail.",
  },
};
